import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { FaThumbsUp, FaRegThumbsUp } from 'react-icons/fa';
import styles from './VideoPage.module.css';
import apiRequest from '../../lib/apiRequest';
import logger from '../../lib/logger';
import { AuthContext } from '../../context/authContext';

const VideoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn, user } = useContext(AuthContext);

  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [likeCount, setLikeCount] = useState(0);
  const [likedByUser, setLikedByUser] = useState(false);
  const [liking, setLiking] = useState(false);

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentError, setCommentError] = useState('');

  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const res = await apiRequest(`/videos/selfieVideos/${id}`);
        setVideo(res.data.video);
        // setLikeCount(res.data.video.likeCount || 0);
      } catch (err) {
        logger.error('Failed to fetch video:', err);
        setError(err.response?.data?.message || 'Could not load video. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    const fetchLikes = async () => {
      try {
        const res = await apiRequest(`/videos/selfieVideos/${id}/likes`);
        setLikeCount(res.data.likes || 0);
        if (isLoggedIn && res.data.likedByUser !== undefined) {
          setLikedByUser(res.data.likedByUser);
        } else {
          setLikedByUser(false); // guests never liked
        }
      } catch (err) {
        logger.error('Failed to fetch like info:', err);
      }
    };

    const fetchComments = async () => {
      try {
        const res = await apiRequest.get(`/comments/${id}`);
        setComments(res.data.comments || []);
      } catch (err) {
        logger.error('Failed to fetch comments:', err);
      }
    };

    fetchVideo();
    fetchLikes();
    fetchComments();
  }, [id]);

  const handleToggleLike = async () => {
    if (!isLoggedIn) return navigate('/login');
    if (liking) return;
    setLiking(true);
    try {
      const res = await apiRequest.post(`/videos/selfieVideos/${id}/toggle-like`);
      if (res.data.success) {
        setLikedByUser((prev) => !prev);
        setLikeCount((prev) => prev + (likedByUser ? -1 : 1));
      }
    } catch (err) {
      logger.error('Error toggling like:', err);
    } finally {
      setLiking(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    try {
      const res = await apiRequest.delete(`/comments/${commentId}`);
      if (res.data.success) {
        setComments((prev) =>
          prev.filter((c) => c._id !== commentId && c.parentComment !== commentId)
        );
      }
    } catch (err) {
      logger.error('Error deleting comment:', err);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!isLoggedIn) return navigate('/login');
    if (!newComment.trim()) return;
    setCommentLoading(true);
    setCommentError('');
    try {
      const res = await apiRequest.post(`/comments/create`, { content: newComment, videoId: id });
      if (res.data.success) {
        setComments((prev) => [res.data.comment, ...prev]);
        setNewComment('');
      } else {
        setCommentError(res.data.message || 'Failed to post comment.');
      }
    } catch (err) {
      logger.error('Error posting comment:', err);
      setCommentError(err.response?.data?.message || 'Error posting comment.');
    } finally {
      setCommentLoading(false);
    }
  };

  const handleReplySubmit = async (e, parentId) => {
    e.preventDefault();
    if (!replyContent.trim()) return;
    try {
      const res = await apiRequest.post(`/comments/create`, {
        content: replyContent,
        videoId: id,
        parentComment: parentId,
      });
      if (res.data.success) {
        setComments((prev) => [res.data.comment, ...prev]);
        setReplyContent('');
        setReplyingTo(null);
      }
    } catch (err) {
      logger.error('Error posting reply:', err);
    }
  };

  const renderComment = (comment, depth = 0) => {
    const isReplying = replyingTo === comment._id;

    return (
      <div key={comment._id} className={styles.commentWrapper}>
        <div
          className={styles.commentItem}
        >
          <div className={styles.commentHeader}>
            <div className={styles.avatar}>
              {comment.user?.username?.[0]?.toUpperCase() || 'A'}
            </div>
            <div className={styles.commentUserMeta}>
              <span className={styles.commentUser}>{comment.user?.username || 'Anonymous'}</span>
              <span className={styles.commentDate}>
                {comment.createdAt ? format(new Date(comment.createdAt), 'PPP p') : 'Unknown date'}
              </span>
            </div>
          </div>
          <p className={styles.commentContent}>{comment.content}</p>
          {isLoggedIn && (
            <div className={styles.commentActions}>
              <button
                className={styles.replyButton}
                onClick={() => {
                  setReplyContent('');
                  setReplyingTo(isReplying ? null : comment._id);
                }}
              >
                {isReplying ? 'Cancel' : 'Reply'}
              </button>
              {video?.user === user?._id && (
                <button
                  className={styles.deleteButton}
                  onClick={() => handleDeleteComment(comment._id)}
                >
                  Delete
                </button>
              )}
            </div>
          )}
          {isReplying && (
            <form onSubmit={(e) => handleReplySubmit(e, comment._id)} className={styles.replyForm}>
              <textarea
                className={styles.commentTextarea}
                placeholder="Write a reply..."
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                rows={2}
              />
              <button type="submit" className={styles.commentButton}>
                Reply
              </button>
            </form>
          )}
        </div>
        {comments
          .filter((c) => c.parentComment === comment._id)
          .length > 0 && (
            <div className={styles.replies}>
              {comments
                .filter((c) => c.parentComment === comment._id)
                .map((child) => renderComment(child, depth + 1))}
            </div>
          )}
      </div>
    );
  };

  if (loading) return <div className={styles.loadingMsg}>Loading video...</div>;
  if (error) return <div className={styles.errorMsg}>{error}</div>;
  if (!video) return <div className={styles.errorMsg}>Video not found.</div>;

  const humanReadableDate = video.updatedAt
    ? format(new Date(video.updatedAt), 'PPP p')
    : 'Unknown date';

  return (
    <div className={styles.videoContainer}>
      <h1 className={styles.videoTitle}>{video.title}</h1>
      <div className={styles.videoDetails}>
        <p>Views: {video.views || 0}</p>
        <p>Uploaded: {humanReadableDate}</p>
      </div>
      {video.description && (
        <div className={styles.videoDescription}>{video.description}</div>
      )}
      {video.cdnUrl && (
        <video src={video.cdnUrl} controls className={styles.videoPlayer}></video>
      )}
      <div className={styles.likeContainer}>
        <button
          className={`${styles.likeButton} ${likedByUser ? styles.liked : ''}`}
          onClick={handleToggleLike}
          disabled={liking}
          title={likedByUser ? 'Unlike' : 'Like'}
        >
          {likedByUser ? (
            <FaThumbsUp className={styles.likeIconFilled} />
          ) : (
            <FaRegThumbsUp className={styles.likeIconOutline} />
          )}
        </button>
        <span className={styles.likeCount}>{likeCount}</span>
      </div>

      <div className={styles.commentsSection}>
        <h2 className={styles.commentsTitle}>Comments</h2>
        {isLoggedIn ? (
          <form onSubmit={handleCommentSubmit} className={styles.commentForm}>
            <input
              type="text"
              className={styles.commentInput}
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
            <button type="submit" className={styles.commentButton} disabled={commentLoading}>
              {commentLoading ? 'Posting...' : 'Post Comment'}
            </button>
          </form>
        ) : (
          <p className={styles.loginPrompt}>
            <Link to="/login" className={styles.loginLink}>
              Log in
            </Link>{' '}
            to post a comment.
          </p>
        )}
        {commentError && <p className={styles.commentError}>{commentError}</p>}
        <div className={styles.commentsList}>
          {comments.length === 0 ? (
            <p className={styles.noComments}>No comments yet.</p>
          ) : (
            comments
              .filter((comment) => !comment.parentComment)
              .map((comment) => renderComment(comment))
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoPage;
